import { Components } from '.generated/templates/Feature.Components.model';
import {
  ImageField,
  LinkField,
  Text,
  Image as SitecoreImage,
} from '@sitecore-jss/sitecore-jss-react';
import Button from 'components/helpers/Button';
import {
  logoNavigationGtmPush,
  menuLocation,
  pageNavigationDataLayerHandler,
} from 'lib/util/gtm-datalayer';
import { classnames } from 'tailwindcss-classnames';
import NextLink from 'next/link';
import HeadingTag from 'components/helpers/HeadingTag';
import {
  CollectionStackItem,
  NavigationItem,
  NavigationLink,
  headerOrLink,
} from 'components/helpers/NmdpSite/Header/NmdpSiteHeaderTypes';
import { useEffect, useState } from 'react';

export type PublicisHeaderProps = {
  fields?: {
    root?: Components.Site.Fields.PublicisHeader & {
      children?: headerOrLink[] & {
        children?: NavigationItem[];
      };
      fields: {
        giveMoneyButton?: LinkField;
        joinRegistryButton?: LinkField;
      };
    };
  };
};

const maxWScreen2xl = `max-w-screen-2xl`;
const borderGrayWhisper = 'border-gray-whisper';
const borderBottom2 = 'border-b-2';

interface DesktopNavigationItemProps {
  item: NavigationLink;
}

const DesktopNavigationItem: React.FC<DesktopNavigationItemProps> = ({ item }) => {
  return (
    <>
      {item.fields?.navigationItemLink && (
        <li role="menuitem" className={classnames('inline-block', 'px-4', 'nav-direct-link')}>
          <a
            href={item.fields?.navigationItemLink?.value?.href}
            aria-label="menu item"
            className={classnames(
              'my-2',
              'mx-2',
              'w-full',
              'sm:w-auto',
              'text-center',
              'md:text-left'
            )}
          >
            {item.displayName}
          </a>
        </li>
      )}
    </>
  );
};

const DesktopNavigation = ({ desktopItems }: { desktopItems: React.ReactNode }) => {
  return (
    <ul
      className={classnames(maxWScreen2xl)}
      role="menubar"
      aria-label="Network Resource Center main menu"
    >
      {desktopItems}
    </ul>
  );
};

const PublicisHeader = ({ fields }: PublicisHeaderProps): JSX.Element => {
  const [collectionStack, setCollectionStack] = useState<CollectionStackItem[]>([]);
  useEffect(() => {
    setCollectionStack([
      {
        linkItems: fields?.root?.children as NavigationLink[],
        backToDisplayName: '',
      },
    ]);
  }, [fields?.root?.children]);
  const getDesktopItems = (_orgLinkItems: NavigationLink[]) => {
    return (_orgLinkItems || []).map((item: NavigationLink) => (
      <DesktopNavigationItem key={item.id} item={item} />
    ));
  };

  const giveMoneyButton = fields?.root?.fields?.giveMoneyButton as LinkField;
  const joinRegistryButton = fields?.root?.fields?.joinRegistryButton as LinkField;
  const logo = fields?.root?.fields?.logo as ImageField;
  const desktopItems = getDesktopItems(collectionStack[0]?.linkItems);
  const publicisImage = fields?.root?.fields?.publicisImage as ImageField;
  return (
    <div
      data-component="authorable/site/header"
      className={classnames('flex', 'flex-col', 'h-auto')}
    >
      <div className="nmdp-header">
        <div className={classnames('block', 'max-w-screen-2xl', 'mx-auto')}>
          <div className={classnames(borderBottom2, borderGrayWhisper, 'justify-center', 'flex')}>
            <NextLink href="/" locale={false}>
              <a
                href="/"
                aria-label="Menu item"
                onClick={() => {
                  logoNavigationGtmPush(menuLocation[1]);
                }}
              >
                <SitecoreImage field={logo} className="logo-desktop" />
              </a>
            </NextLink>
            <NextLink href="https://publicis.nmdp.org/" locale={false}>
              <a
                href="https://publicis.nmdp.org/"
                aria-label="Menu item"
                onClick={() => {
                  logoNavigationGtmPush(menuLocation[1], 'publicisLogo');
                }}
              >
                <SitecoreImage field={publicisImage} className="logo-desktop" />
              </a>
            </NextLink>
          </div>
          {desktopItems && (
            <nav
              className={classnames(
                'md:flex',
                'flex-wrap',
                'justify-center',
                'px-4',
                'mb-6',
                'mt-4',
                'hidden'
              )}
            >
              <DesktopNavigation desktopItems={desktopItems} />
            </nav>
          )}
          <div
            className={classnames('flex', 'flex-wrap', 'justify-center', 'px-4', 'pt-6', 'md:pt-0')}
          >
            <HeadingTag
              headingLevel="h2"
              className={classnames('heading-xl', 'text-center', 'mb-4')}
            >
              <Text field={fields?.root?.fields?.headerTitle} />
            </HeadingTag>
          </div>
          <div className={classnames('flex', 'flex-wrap', 'justify-center', 'px-4')}>
            {giveMoneyButton?.value && (
              <Button
                linkField={giveMoneyButton}
                className={classnames(
                  'my-2',
                  'mx-2',
                  'w-full',
                  'sm:w-auto',
                  'text-center',
                  'md:text-left'
                )}
                onGtmPush={(e) => {
                  pageNavigationDataLayerHandler(e);
                }}
              />
            )}
            {joinRegistryButton?.value && (
              <Button
                linkField={joinRegistryButton}
                className={classnames(
                  'my-2',
                  'mx-2',
                  'w-full',
                  'sm:w-auto',
                  'text-center',
                  'md:text-left'
                )}
                onGtmPush={(e) => {
                  pageNavigationDataLayerHandler(e);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicisHeader;
